import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";
import { BlockEventList } from "../../components/block-event-list";
import { Modal } from "../../components/modal";
import { Pagination } from "../../components/pagination";
import { isBlockEventHistoryItem } from "../../utils/is-block-event-history-event";

import type {
  BlockEventHistory,
  FreeBlockEventSuccessResponse,
} from "@sperrdatei/dto/types";

type VerifyBlockPageProps = object;

export const VerifyBlockPage: React.FC<VerifyBlockPageProps> = () => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState<BlockEventHistory[]>([]);
  const [result, setResult] = useState<
    FreeBlockEventSuccessResponse | undefined
  >(undefined);
  const [selectedNumber, setSelectedNumber] = useState<
    BlockEventHistory | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = useState(0);
  const [maxPage, setMaxPage] = useState(9999);

  const updateBlockEvents = useCallback(async () => {
    try {
      const response = await apiFetch(
        `/api/block-event/not-verified/${currentPage}`,
      );

      if (response.ok) {
        const { events, pagination } = await response.json();
        setMaxPage(pagination.pages);
        setEvents([...events]);
      }
    } catch {
      navigate("/error");
    }
  }, [apiFetch, navigate, currentPage]);

  useEffect(() => {
    updateBlockEvents();
  }, [updateBlockEvents]);

  const VerifyBlockEventModal: React.FC = () => {
    const id = selectedNumber ? selectedNumber.id : -1;
    const fullNumber = selectedNumber
      ? `${selectedNumber.prefix}-${selectedNumber.number}`
      : "";
    return (
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Block Prüfen"
        spreadButtons={true}
        primaryAction={{
          label: "Akzeptieren",
          buttonStyle: "secondary",
          showLoadingIndicator: isLoading,
          onClick: async () => {
            setIsLoading(true);
            const response = await apiFetch(`/api/block-event/verify/${id}`, {
              method: "PATCH",
            });
            setIsLoading(false);

            if (response.ok) {
              updateBlockEvents();
              setIsModalOpen(false);
              return;
            }

            navigate("/error");
            return;
          },
        }}
        secondaryAction={{
          label: "Ablehnen",
          buttonStyle: "danger",
          showLoadingIndicator: isLoading,
          onClick: async () => {
            setIsLoading(true);
            const response = await apiFetch(`/api/block-event/${id}`, {
              method: "DELETE",
            });
            setIsLoading(false);

            if (response.ok) {
              setResult(await response.json());
              updateBlockEvents();
              setIsModalOpen(false);
              return;
            }

            navigate("/error");
            return;
          },
        }}
      >
        <p>
          Wollen Sie die Sperrung aller im Rufnummernblock{" "}
          <strong style={{ whiteSpace: "nowrap" }}>{fullNumber}</strong>{" "}
          enthaltenen Nummern akzeptieren?
        </p>
      </Modal>
    );
  };

  return (
    <>
      {result ? (
        <>
          <h6>Block abgelehnt</h6>
          <p>
            <strong>Ergebnis</strong>: <br />
            <span>
              <strong>{result.freed}</strong>{" "}
              {result.freed > 1 ? "Nummern wurden" : "Nummer wurde"} wieder
              freigegeben.
            </span>
          </p>
          <button
            type="button"
            className="btn btn-primary mb-3"
            onClick={() => setResult(undefined)}
          >
            OK
          </button>
        </>
      ) : (
        <div className="container ms-0">
          <VerifyBlockEventModal />
          <h3 className="page-caption mb-3">Block prüfen</h3>{" "}
          <p>Hier werden alle nicht geprüften Rufnummernblöcke angezeigt</p>
          <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 p-0">
            <BlockEventList
              events={events}
              selectEvent={(event) => {
                if (isBlockEventHistoryItem(event)) {
                  setSelectedNumber(event);
                  setIsModalOpen(true);
                }
              }}
            />
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              maxPage={maxPage}
            />
          </div>
        </div>
      )}
    </>
  );
};
